/**
 * Notification types that are queued via `NotificationService.schedule` and drained by the jobs runner.
 * The schedule handler map is exhaustively typed over exactly these.
 */
export const SCHEDULED_NOTIFICATION_TYPES = [
  'shipment_creation',
  'shipment_assignment',
  'shipment_unassignment',
  'shipment_update_package',
  'shipment_update_time',
  'shipment_update_address',
  'shipment_update_info',
  'shipment_delayed',
  'leg_status_update_collection',
  'leg_status_update_delivery',
  'shipment_alert',
  'document_creation',
  'support_access_granted',
  'shipment_cancellation',
] as const;

export type SCHEDULED_NOTIFICATION_TYPE = (typeof SCHEDULED_NOTIFICATION_TYPES)[number];

// All notification types: the schedulable ones plus those sent directly (not via the schedule queue).
export const NOTIFICATION_TYPES = [...SCHEDULED_NOTIFICATION_TYPES, 'role_reassign_complete'] as const;

export type NOTIFICATION_TYPE = (typeof NOTIFICATION_TYPES)[number];

export type NotificationRow = {
  id: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  read_at: null | string;
  archived_at: null | string;
  type: NOTIFICATION_TYPE;
  payload: Record<string, unknown>;
  payload_hash: string;
  created_window: string; // tstzrange
};

export type Notification = NotificationRow;

export type NotificationRowInsert = Partial<NotificationRow> & NonNullable<Pick<NotificationRow, 'user_id' | 'type'>>;

export type NotificationRowUpdate = Omit<Partial<NotificationRow>, 'id'>;

export type NotificationSend = Omit<NotificationRowInsert, 'user_id'>;

export type NotificationScheduleRow = {
  id: string;
  created_at: string;
  sender_id: string | null;
  type: SCHEDULED_NOTIFICATION_TYPE;
  params: Record<string, unknown>;
  delay: number;
  finished_at: null | string;
  status: 'pending' | 'success' | 'failed';
  params_hash: string;
};

export type NotificationSchedule = NotificationScheduleRow;

export type NotificationScheduleRowInsert = Partial<NotificationScheduleRow> & NonNullable<Pick<NotificationScheduleRow, 'sender_id' | 'type' | 'params'>>;

export type NotificationScheduleRowUpdate = Omit<Partial<NotificationScheduleRow>, 'id'>;
